import MenuIcon from '@mui/icons-material/Menu';
import { Box, Button, Drawer, IconButton, useMediaQuery, useTheme } from '@mui/material';
import { useAtomValue, useSetAtom } from 'jotai';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useInstantSearch } from 'react-instantsearch';
import { familyLevelImage } from '../discovery.utils';
import { discoveryViewAtom, familyPathAtom } from '../state';
import { FAMILY_LEVEL_ATTRIBUTES, applyFamilyPath, familyLevelLabelKey, familyPathFromRefinementList } from './family-level-facets';
import CompactDepartments from './CompactDepartments';
import DepartmentsColumn from './DepartmentsColumn';
import RegisterFamilyLevelRefinements from './RegisterFamilyLevelRefinements';

const LAST_LEVEL_DEPTH = FAMILY_LEVEL_ATTRIBUTES.length - 1;

export default function DiscoveryDepartmentsNavigation(): React.JSX.Element {
  const { t } = useTranslation(['common', 'discovery', 'product-family', 'product-classification']);
  const theme = useTheme();
  const isCompact = useMediaQuery(theme.breakpoints.down('lg'));
  const discoveryView = useAtomValue(discoveryViewAtom);
  const familyPath = useAtomValue(familyPathAtom);
  const setFamilyPath = useSetAtom(familyPathAtom);
  const { indexUiState, setIndexUiState } = useInstantSearch();
  const indexPath = familyPathFromRefinementList(indexUiState.refinementList);
  const indexKey = indexPath.join('\0');
  const atomKey = familyPath.join('\0');
  const [open, setOpen] = useState(false);
  const [path, setPath] = useState<string[]>([]);

  useEffect(() => {
    if (indexKey !== atomKey) {
      setFamilyPath(indexPath);
    }
  }, [indexKey, atomKey]);

  const openDrawer = (): void => {
    setPath([]);
    setOpen(true);
  };

  const closeDrawer = (): void => {
    setOpen(false);
    setPath([]);
  };

  const selectPath = (nextPath: string[]): void => {
    applyFamilyPath(setIndexUiState, nextPath);
    closeDrawer();
  };

  const hoverItem = (depth: number, code: string): void => {
    setPath((current) => {
      if (current[depth] === code && current.length === depth + 1) {
        return current;
      }

      return [...current.slice(0, depth), code];
    });
  };

  const truncatePath = (depth: number): void => {
    setPath((current) => (current.length <= depth ? current : current.slice(0, depth)));
  };

  const rootSeeAllLabel = discoveryView === 'PRODUCTS' ? t('common:all_products') : t('discovery:all_suppliers');

  return (
    <>
      <RegisterFamilyLevelRefinements />
      {isCompact ? (
        <IconButton
          aria-label={t('discovery:departments')}
          onClick={openDrawer}
          sx={{
            width: 40,
            height: 40,
            border: '1px solid',
            borderColor: 'outline.main',
            color: 'primary.main',
          }}
        >
          <MenuIcon />
        </IconButton>
      ) : (
        <Button variant="outlined" startIcon={<MenuIcon />} onClick={openDrawer}>
          {t('discovery:departments')}
        </Button>
      )}
      {!isCompact ? (
        <Drawer
          anchor="left"
          open={open}
          onClose={closeDrawer}
          slotProps={{
            paper: {
              sx: {
                display: 'flex',
                flexDirection: 'row',
                gap: 0,
                p: 1,
                boxSizing: 'border-box',
                width: 'fit-content',
                maxWidth: '100%',
                height: '100%',
                overflowX: 'auto',
                backgroundColor: 'transparent',
                boxShadow: 'none',
              },
            },
          }}
        >
          <Box
            sx={{
              display: 'flex',
              width: 'max-content',
              height: '100%',
              borderRadius: '24px',
              overflow: 'hidden',
              backgroundColor: 'surfaceContainerLow.main',
            }}
          >
            <DepartmentsColumn
              key="root"
              pathPrefix={[]}
              selectedCode={path[0]}
              title={t('discovery:departments')}
              width={300}
              showDivider={path.length > 0}
              onClose={closeDrawer}
              onHover={(code) => hoverItem(0, code)}
              onSelect={(code) => selectPath([code])}
              onSeeAll={() => selectPath([])}
              onSeeAllHover={() => truncatePath(0)}
              seeAllLabel={rootSeeAllLabel}
            />
            {path.map((_, index) => {
              const prefix = path.slice(0, index + 1);
              const parentCode = prefix[prefix.length - 1];

              return (
                <DepartmentsColumn
                  key={prefix.join('\0')}
                  pathPrefix={prefix}
                  selectedCode={path[prefix.length]}
                  title={t(familyLevelLabelKey(parentCode, prefix.length - 1))}
                  image={familyLevelImage(parentCode)}
                  width={300}
                  showDivider={prefix.length < path.length}
                  onHover={(code) => hoverItem(prefix.length, code)}
                  onSelect={(code) => selectPath([...prefix, code])}
                  onSeeAll={() => selectPath(prefix)}
                  onSeeAllHover={() => truncatePath(prefix.length)}
                  onCollapse={() => setPath((current) => current.slice(0, prefix.length - 1))}
                />
              );
            })}
          </Box>
        </Drawer>
      ) : (
        <CompactDepartments
          open={open}
          path={path}
          onClose={closeDrawer}
          onBack={() => setPath((current) => current.slice(0, -1))}
          onSelect={(code) => {
            if (path.length < LAST_LEVEL_DEPTH) {
              setPath((current) => [...current, code]);
              return;
            }

            selectPath([...path, code]);
          }}
          onSeeAll={() => selectPath(path)}
          onEmpty={() => selectPath(path)}
        />
      )}
    </>
  );
}
